/**
 * Header user pill (S-header-user-pill): a clickable identity pill in the app shell
 * header showing the current user's name and a generated initials avatar. The dropdown
 * carries the theme selector, the language selector, the panel switcher (when the shell
 * passes one), an edit-own-profile entry, and the sign-out action.
 *
 * Identity comes from the existing AuthProvider current-user (`GET /auth/me`) — never
 * hardcoded. The theme/language controls reuse the SAME mechanisms as the standalone
 * header switchers (`useThemeStore` and `i18n.changeLanguage`) so the two stay in sync;
 * sign-out reuses the AuthProvider `logout()` (which POSTs /auth/logout) and then sends
 * the user to /login, matching the existing logout behaviour.
 *
 * Avatar is an INITIALS placeholder only — photo upload is deferred (PROD-016); there is
 * no avatar/photo storage in this MVP.
 */

import { DownOutlined, LogoutOutlined, SwapOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Dropdown, Space, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { MenuProps } from 'antd';

import { useAuth } from '@/shared/auth/AuthProvider';
import { SUPPORTED_LOCALES } from '@/shared/lib/i18n';
import { initialsFrom } from '@/shared/lib/initials';
import { useThemeStore } from '@/shared/stores/theme-store';

const { Text } = Typography;

export interface UserPillProps {
  /**
   * The panel switcher's home is the account menu (gate-panel feedback 21.08 — it moved here from
   * the shells' own chrome): each shell passes its counterpart's label + navigation, or null when
   * the user cannot use the other panel.
   */
  panelSwitch?: { label: string; onClick: () => void } | null;
}

export function UserPill({ panelSwitch = null }: UserPillProps): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { user, logout } = useAuth();
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);

  // The pill is authenticated chrome; without a current user there is nothing to show.
  if (!user) {
    return null;
  }

  // Identity is the user's full name from /auth/me (S-header-user-pill); when no name is
  // recorded (null/blank), fall back to the email so the pill and its initials are never
  // blank. The avatar initials derive from whichever name is displayed.
  const displayName = user.fullName?.trim() ? user.fullName.trim() : user.email;

  const items: MenuProps['items'] = [
    {
      key: 'theme',
      label: t('userMenu.theme'),
      children: [
        { key: 'theme:light', label: t('userMenu.themeLight'), disabled: mode === 'light' },
        { key: 'theme:dark', label: t('userMenu.themeDark'), disabled: mode === 'dark' },
      ],
    },
    {
      key: 'language',
      label: t('userMenu.language'),
      // Locale labels are the language endonyms (native-script names) sourced from the
      // translation files via `t('locale.<lng>')` — all user-facing text comes from the
      // locale files (R-18.310). An endonym is the same in every UI language, so each
      // locale file carries the same value for a given key.
      children: SUPPORTED_LOCALES.map((lng) => ({
        key: `language:${lng}`,
        label: t(`locale.${lng}`),
        disabled: i18n.resolvedLanguage === lng,
      })),
    },
    { type: 'divider' },
    ...(panelSwitch !== null
      ? [
          { key: 'panel-switch', icon: <SwapOutlined />, label: panelSwitch.label },
          { type: 'divider' as const },
        ]
      : []),
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('userMenu.editProfile'),
    },
    { type: 'divider' },
    {
      key: 'sign-out',
      icon: <LogoutOutlined />,
      label: t('userMenu.signOut'),
      danger: true,
    },
  ];

  const onClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'theme:light') {
      setMode('light');
    } else if (key === 'theme:dark') {
      setMode('dark');
    } else if (key.startsWith('language:')) {
      void i18n.changeLanguage(key.slice('language:'.length));
    } else if (key === 'panel-switch') {
      panelSwitch?.onClick();
    } else if (key === 'profile') {
      navigate('/profile');
    } else if (key === 'sign-out') {
      void logout().then(() => navigate('/login', { replace: true }));
    }
  };

  return (
    <Dropdown menu={{ items, onClick }} trigger={['click']} placement="bottomRight">
      {/* A native <button> is keyboard-focusable and Enter/Space-operable with no manual
          tabIndex (R-18.26/R-18.29); it carries the AntD Space layout inside. Button chrome is
          stripped so it reads as the inline pill, not a default button. */}
      <button
        type="button"
        aria-label={t('userMenu.trigger')}
        style={{
          cursor: 'pointer',
          paddingInline: 8,
          borderRadius: token.borderRadius,
          background: 'none',
          border: 'none',
          font: 'inherit',
          color: 'inherit',
        }}
      >
        <Space size={8} align="center">
          <Avatar size="small" style={{ backgroundColor: token.colorPrimary }}>
            {initialsFrom(displayName)}
          </Avatar>
          <Text style={{ whiteSpace: 'nowrap' }}>{displayName}</Text>
          <DownOutlined style={{ fontSize: 10, color: token.colorTextSecondary }} />
        </Space>
      </button>
    </Dropdown>
  );
}
